import { Content } from "antd/es/layout/layout";
import React, { useState } from "react";
import Header from "../../components/common/Header";
import PropTypes from "prop-types";
import { Avatar, DatePicker, Modal, Tag } from "antd";
import { useGetSalaryQuery } from "../../service/salaryService";
import { EyeOutlined } from "@ant-design/icons";
import Datatable from "../../components/common/Datatable";
import { useSelector } from "react-redux";
import SalarySlip from "./SalarySlip";
import dayjs from "dayjs";
import IconButton from "../../components/common/IconButton";
import moment from "moment/moment";
import { convertCurrency } from "../../utils/helpers";

const initialSalaryOverviewModalState = {
  data: null,
  open: false,
};

function Payslip() {
  const { data } = useGetSalaryQuery();
  const [salaryOverviewModal, setSalaryOverviewModal] = useState(
    initialSalaryOverviewModalState
  );
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const user = useSelector((state) => state.application.user);

  const handleView = (record) => {
    setSalaryOverviewModal({ data: record, open: true });
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
  };

  const filteredData = data?.data?.filter(
    (item) =>
      item.month === selectedMonth.format("MMMM") &&
      item.year === selectedMonth.year()
  );
  const current = filteredData?.[0];
  const columns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (_, record) => `${record.month} ${record.year}`,
    },
    {
      title: "Status",
      dataIndex: "isPaid",
      key: "isPaid",
      render: (status) => (
        <Tag color={status === true ? "green" : "blue"}>
          {status === true ? "Paid" : "Processing"}
        </Tag>
      ),
    },
    {
      title: "Net Salary",
      dataIndex: "netSalary",
      key: "netSalary",
      render: (amount) => (amount ? convertCurrency(amount) : "--"),
    },
    {
      title: "Actions",
      key: "view",
      render: (_, record) =>
        record?.isPaid === true ? (
          <div className="flex gap-2">
            <IconButton
              title="View"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </div>
        ) : (
          <div>-</div>
        ),
    },
  ];

  return (
    <div>
      <Modal
        open={salaryOverviewModal.open}
        onCancel={() => setSalaryOverviewModal(initialSalaryOverviewModalState)}
        width={900}
        style={{ top: 20 }}
        footer={null}
        closable={false}
      >
        <div className="h-[85vh] overflow-auto">
          <SalarySlip
            data={salaryOverviewModal.data}
            onClose={() =>
              setSalaryOverviewModal(initialSalaryOverviewModalState)
            }
          />
        </div>
      </Modal>
      <div className="flex justify-between items-center">
        <Header title="Earnings Overview" />
        <div>
          <DatePicker
            picker="month"
            onChange={handleMonthChange}
            value={selectedMonth}
            allowClear={false}
          />
        </div>
      </div>
      <Content className="mt-7">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-[2fr,3fr] gap-2">
            <div className="bg-white border border-gray-200 rounded-lg flex flex-col md:flex-row gap-6 items-center">
              <div className="flex flex-col items-center text-center md:w-[40%]">
                <Avatar className="p-14 rounded-full flex justify-center items-center shadow-sm bg-gray-300">
                  <div className="text-6xl">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </div>
                </Avatar>
                <h2 className="mt-4 text-lg font-bold text-gray-800">
                  {user?.firstName} {user?.lastName}
                </h2>
                <p className="text-blue-500 font-medium text-sm">
                  {user?.meta?.department}
                </p>
              </div>
              <div className="flex-1 w-full grid grid-cols-1 gap-4 text-sm text-gray-700">
                <DetailItem
                  label="Net Salary (This Month)"
                  value={
                    current?.netSalary
                      ? convertCurrency(current?.netSalary)
                      : `₹0`
                  }
                />
                <DetailItem
                  label="Payment Status"
                  value={current?.isPaid ? "Received" : "Not Received"}
                />

                <DetailItem
                  label="Pay Period"
                  value={moment(selectedMonth).format("MMMM YYYY")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <SectionCard title="Earnings" total={current?.grossSalary}>
                <DetailRow label="Basic" value={current?.basicPay} />
                <DetailRow label="House Rent Allowance" value={current?.hra} />
                <DetailRow
                  label="Medical Allowance"
                  value={current?.medicalAllowance}
                />
                <DetailRow
                  label="Conveyance Allowance"
                  value={current?.conveyance}
                />
              </SectionCard>

              <SectionCard title="Deductions" total={current?.totalDeductions}>
                <DetailRow label="Provident Fund" value={current?.pf} />
                <DetailRow
                  label="Professional Tax"
                  value={current?.professionalTax}
                />
                <DetailRow label="Income Tax" value={current?.incomeTax} />
              </SectionCard>
            </div>
          </div>
          <div className="bg-white border border-gray-200 p-4 rounded-lg">
            <h2 className="text-base font-semibold text-gray-800 mb-6 border-b pb-3">
              Salary Statements
            </h2>
            <div>
              <Datatable columns={columns} tableData={data?.data} />
            </div>
          </div>
        </div>
      </Content>
    </div>
  );
}

const DetailItem = ({ label, value }) => (
  <div className="border-l-2 border--300 px-2">
    <div className="flex flex-col gap-2 justify-between">
      <span>{label}</span>
      <span className="text-gray-900 font-semibold">{value}</span>
    </div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between">
    <span>{label}</span>
    <span className="font-semibold">
      {value ? convertCurrency(value) : "₹0"}
    </span>
  </div>
);

const SectionCard = ({ title, total, children }) => (
  <div className="bg-white border border-gray-200 p-4 rounded-lg flex flex-col h-full">
    <h2 className="text-base font-semibold mb-4 border-b pb-2 text-gray-800">
      {title}
    </h2>

    <div className="flex-1 space-y-3 text-gray-700 text-sm">{children}</div>

    <div className="pt-4 mt-4 border-t text-gray-700 text-right text-base font-bold">
      Total: {total ? convertCurrency(total) : "₹0"}
    </div>
  </div>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  children: PropTypes.node,
};

export default Payslip;
